import { useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { isEqual, maxBy, uniqueId } from "lodash-es";

import { loadFlow } from "src/api";
import { jobsApi } from "src/api/exporterApi";
import { Flow } from "src/api/flowTypes";
import { flowKeys } from "src/api/queries";
import { DecisionEnvironment } from "src/api/types";
import { ActionButtonProps } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import {
  useJobSources,
  useRunJob,
  useUpdateJobDestination,
} from "src/jobs/api/queries";
import {
  isJobDestinationDB,
  mapOutput,
} from "src/jobs/common/DestinationDropdown";
import { isJobRunnable } from "src/jobs/jobUtils";
import {
  Job,
  JobDestinationDB,
  JobRun,
  JobRunStatusType,
} from "src/jobs/types";
import { useEnvironmentFilterAsQueryParam } from "src/jobs/useEnvironmentFilterAsQueryParam";
import {
  useFlowContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import { errorMessage } from "src/utils/stringUtils";

export const useJobSource = <S>(jobId: string, sourceId?: string) => {
  const { workspace } = useWorkspaceContext();
  const sources = useJobSources(workspace.base_url!, jobId);

  if (!sourceId) return;

  return sources.data?.find((source) => source.id === sourceId) as S;
};

const getUsedVersionIds = (job: Job): string[] => {
  if (job.active_traffic_policy) {
    return job.active_traffic_policy.policy.flow_versions.map(
      (version) => version.flow_version_id,
    );
  }

  return job.flow_version_id ? [job.flow_version_id] : [];
};

export const useRunJobHandler = (job: Job) => {
  const { isOpen, closeModal, afterLeave, openModal } = useModal();
  const jobRunnable = isJobRunnable(job);

  const { flow, workspace } = useFlowContext();
  const queryClient = useQueryClient();
  const updateJobDestinationMutation = useUpdateJobDestination(
    workspace.base_url!,
    job.id,
  );

  const runJob = useRunJob(workspace.base_url!, job.id, flow.id);

  const [, setEnvironmentFilter] = useEnvironmentFilterAsQueryParam();

  const refreshDestinationMapping = async (
    refreshedJob: Job,
    refreshedFlow: Flow,
  ): Promise<void> => {
    if (!refreshedJob.active_destination) return;
    if (!isJobDestinationDB(refreshedJob.active_destination)) return;
    const activeDestination: JobDestinationDB = refreshedJob.active_destination;

    const usedVersionIds: string[] = getUsedVersionIds(refreshedJob);
    const usedVersions = refreshedFlow.versions.filter((version) =>
      usedVersionIds.includes(version.id),
    );
    const newMapping: JobDestinationDB["configuration"]["mapping"] =
      mapOutput(usedVersions);
    const currentMapping: JobDestinationDB["configuration"]["mapping"] =
      activeDestination.configuration.mapping ?? [];

    if (isEqual(newMapping, currentMapping)) return;

    await updateJobDestinationMutation.mutateAsync({
      id: activeDestination.id,
      etag: activeDestination.etag,
      configuration: {
        ...activeDestination.configuration,
        mapping: newMapping,
      },
    });
  };

  const handleRunJob = async (
    environment: DecisionEnvironment,
  ): Promise<void> => {
    const toastId = uniqueId();
    let createdRun: JobRun | undefined;
    let refreshedJob: Job;
    let refreshedFlow: Flow;

    try {
      refreshedJob = await queryClient.fetchQuery({
        queryKey: ["job", workspace.base_url!, job.id],
        queryFn: async (): Promise<Job> => {
          return (
            await jobsApi.get<Job>(`/jobs/${job.id}`, {
              baseURL: workspace.base_url!,
            })
          ).data;
        },
      });
    } catch (error) {
      toastActions.failure({
        title: "Job run failed",
        description: errorMessage(error),
      });
      return;
    }

    try {
      refreshedFlow = await queryClient.fetchQuery({
        queryKey: flowKeys.detail(flow.id),
        queryFn: async (): Promise<Flow> => loadFlow(flow.id),
      });
    } catch (error) {
      toastActions.failure({
        title: "Job run failed",
        description: errorMessage(error),
      });
      return;
    }

    try {
      await refreshDestinationMapping(refreshedJob, refreshedFlow);
    } catch (error) {
      toastActions.failure({
        title: "Destination mapping refresh failed",
        description: errorMessage(error),
      });
      return;
    }

    try {
      createdRun = await runJob.mutateAsync(environment);
      closeModal();
      toastActions.loading({
        id: toastId,
        title: "Running your Job...",
        description:
          "The process will continue in the background even when this notification is dismissed.",
        duration: Infinity,
      });
      setEnvironmentFilter(environment);
    } catch (error) {
      toastActions.failure({
        id: toastId,
        title: "Job run failed",
        description: errorMessage(error),
      });
    }
    if (!createdRun) return;

    while (
      createdRun.status.type === JobRunStatusType.Running ||
      createdRun.status.type === JobRunStatusType.Writing
    ) {
      await new Promise((r) => setTimeout(r, 5000));
      createdRun = (
        await jobsApi.get<JobRun>(
          `/jobs/${createdRun.job_id}/runs/${createdRun.id}/poll`,
          {
            baseURL: workspace.base_url!,
          },
        )
      ).data;
    }

    if (createdRun.status.type === JobRunStatusType.Completed)
      toastActions.success({ id: toastId, title: "Job run finished" });
    else if (createdRun.status.type === JobRunStatusType.Failed)
      toastActions.failure({ id: toastId, title: "Job run failed" });
    else if (createdRun.status.type === JobRunStatusType.Stopped)
      toastActions.success({ id: toastId, title: "Job run stopped" });
  };

  return {
    onConfirm: handleRunJob,
    isJobRunnable: jobRunnable,
    isOpen,
    afterLeave,
    openModal,
    closeModal,
  };
};

export const useRunJobActionButton = (
  job: Job,
  handler: Pick<
    ReturnType<typeof useRunJobHandler>,
    "isJobRunnable" | "openModal"
  >,
): ActionButtonProps | undefined => {
  const { jobs } = useCapabilities();
  const usedVersions = useUsedVersions(job);

  if (job.status === "active" && jobs.canRunLive) {
    return {
      label: "Run Job",
      dataLoc: "run-job-live-button",
      isDisabled: !handler.isJobRunnable,
      onClick: handler.openModal,
    };
  }

  if (job.status === "inactive" && jobs.canRunSandbox) {
    return {
      label: "Run in Sandbox",
      dataLoc: "run-job-sandbox-button",
      isDisabled: !handler.isJobRunnable,
      onClick: handler.openModal,
      tooltip: {
        body: `You must add ${[
          usedVersions.length === 0 && "at least one Flow version",
          !job.active_source && "a source",
        ]
          .filter(Boolean)
          .join(" and ")} to run the Job in Sandbox`,
        placement: "bottom",
        disabled: handler.isJobRunnable,
      },
    };
  }

  return undefined;
};

export const useUsedVersions = (job?: Job) => {
  const { flow } = useFlowContext();

  return useMemo(() => {
    if (!job) return [];

    const usedVersionIds: string[] = getUsedVersionIds(job);

    return flow.versions.filter((v) => usedVersionIds.includes(v.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.active_traffic_policy?.id, job?.flow_version_id, flow.versions]);
};

export const useDominantVersion = (job: Job) => {
  const usedVersions = useUsedVersions(job);

  return useMemo(() => {
    const policy = job.active_traffic_policy?.policy;
    if (!policy) return usedVersions.at(0);

    const versionWithMostPercentage = maxBy(
      usedVersions,
      (version) =>
        policy.flow_versions.find(
          (flowVersion) => flowVersion.flow_version_id === version.id,
        )?.percentage ?? 0,
    );

    return versionWithMostPercentage ?? usedVersions.at(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.active_traffic_policy?.id, usedVersions]);
};
